import { Global, Collection } from "@sourceflow-uk/sourceflow-sdk";
import SeoHead from "../../components/SeoHead";
import { Helmet } from "react-helmet";
import LatestNews from "../../components/LatestNews";
import { Container } from "reactstrap";
import NewBlogDetails from "../../components/NewBlogDetails";
import NewSubBanner from "../../components/NewSubBanner";
import NewColumnWithForm from "../../components/NewColumnWithForm";
import NewBlogDetails2 from "../../components/NewBlogDetails2";
import MainBanner from "../../components/MainBanner";
import AllBlogs from "../../components/AllBlogs";
import NewFeaturedBlogs from "../../components/NewFeaturedBlogs";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";
import useDynamicPages from "@/hooks/useDynamicPages";

function PostView({ global, page, all_news, related_news }) {
  const breadcrumbList = [
    {
      "@type": "ListItem",
      name: "Lorien",
      position: 1,
      item: { "@type": "Thing", "@id": `https://www.lorienglobal.com/` },
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Insights",
      item: { "@type": "Thing", "@id": `https://www.lorienglobal.com/insights` },
    },
  ];

  return (
    <>
      <SeoHead
        page_name={`${page.title} | Lorien${page.tag.includes("News & Insights") ? " Insights" : "" }`}
        description={page.tag.includes("Case Study") ? `Discover insights from "${page.title}" and how Lorien delivers effective solutions across Technology.` : `Expert insights on ${page.title} from Lorien, specialists in Technology.`}
        canonical_link={`insights/${page.url_slug}`}
        og_image={page.image}
        og_img_alt={`${page.title}`}
        removeUSHrefLang={
          page.country?.includes("United States") || page.country?.includes("All")
            ? false
            : true
        }
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbList,
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            description: page?.short_description || "",
            isFamilyFriendly: "http://schema.org/True",
            image: page?.image || "",
            dateModified: page?.updated_at || "",
            datePublished: page?.created_at || "",
            name: page?.title || "",
            headline: page?.title || "",
            articleBody: page?.content ? page.content.replace(/<[^>]*>/g, "") : "",
            publisher: {
              "@type": "Organization",
              name: "Lorien",
              sameAs: "https://www.lorienglobal.com/",
              logo: {
                "@type": "ImageObject",
                url: "https://cdn.sourceflow.co.uk/wijxmc47ccusm0jll37684eh7usc",
              },
            },
            author: {
              "@type": "Person",
              image: page?.author_image || "",
              name: page?.author || "",
            },
          })}
        </script>
      </Helmet>

      <NewSubBanner
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`${page.image ?? "/site-assets/images/sub-banner-insights.png"}`}
        imageALT2={`Overlay Main Banner`}
        displayImage1={false}
        editableContent={false}
        buttonLink={`${
          page.tag.includes("Case Study")
            ? "/insights/case-study"
            : page.tag.includes("Guides & Reports")
            ? "/insights/guides-and-reports"
            : "/insights"
        }`}
        buttonTitle={`${
          page.tag.includes("Case Study")
            ? "Back to Case Study"
            : page.tag.includes("Guides & Reports")
            ? "Back to Guides & Reports"
            : "Back to insights"
        }`}
        post={page}
        buttonLink2={`${
          page.tag.includes("Guides & Reports") && page?.form_id ? "#download-form" : ""
        }`}
        buttonTitle2={`${
          page.tag.includes("Guides & Reports") && page?.form_id ? "Download Report" : ""
        }`}
        ctaLink={page?.cta_link ?? null}
        ctaTitle={page?.cta_title ?? null}
      />

      {page.tag.includes("Guides & Reports") || page.tag.includes("News & Insights") ? (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.guides_and_reports.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`insights`}
          share_heading={`Share Report`}
          className={`py-5 guides-and-reports-page`}
          formId={page?.form_id}
          locale={``}
          successPageUrl={
            page.form_link
              ? page.form_link
              : page.form_attachment
              ? page.form_attachment
              : "/submission-success"
          }
        />
      ) : page.tag.includes("Case Study") ? (
        page.case_study_tabs?.length >= 1 && (
          <NewBlogDetails2
            global={global}
            pathPrefix={`insights_page.new_blog_details_2`}
            page={page}
          />
        )
      ) : (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`insights`}
          locale={``}
        />
      )}

      {page.tag.includes("Guides & Reports") || page.tag.includes("Case Study") ? (
        <>
          <Container>
            <hr />
          </Container>
          <LatestNews
            global={global}
            pathPrefix={`${page.url_slug}.insights_page.guides_and_reports.latest_news`}
            customClass={`py-5 white-card`}
            post={all_news}
            content={
              <h2>
                {page.tag.includes("Guides & Reports")
                  ? "Other Guides and Reports"
                  : "Other Case Studies"}
              </h2>
            }
            buttonLink={``}
            buttonTitle={``}
            content_align={`left`}
            footer_buttonLink={`/insights${
              page.tag.includes("Guides & Reports") ? "/guides-and-reports" : "/case-study"
            }`}
            footer_buttonLink_title={`View All`}
          />
        </>
      ) : null}

      <NewColumnWithForm
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`308d12ba-6cc4-44a5-91e2-76d1d714bf23`}
        successPageUrl={
          page.form_link
            ? page.form_link
            : page.form_attachment
            ? page.form_attachment
            : "/submission-success"
        }
      />
    </>
  );
}

function TagView({ global, page, all_news }) {
  return (
    <>
      <SeoHead
        page_name={`${page.title} | The global house of tech and transformation recruitment | Lorien`}
        canonical_link={`insights/${page.url_slug}`}
      />
      <MainBanner
        global={global}
        pathPrefix={`insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`/site-assets/images/sub-banner-insights.png`}
        imageALT2={`Overlay Main Banner`}
        editableContent={false}
        displayImage1={false}
      />
      <NewFeaturedBlogs
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_featured_blogs`}
        customClass={`py-5 grayGradientBg`}
        content={<h2>Featured {page.title}</h2>}
        post={all_news}
        socialPagePrefix={`insights/${page.url_slug}`}
      />
      <AllBlogs
        global={global}
        pathPrefix={`${page.url_slug}.news_page.all_blogs`}
        customClass={`py-5 lightGreybg`}
        post={all_news}
        ParentNews={false}
        tagName={`${page.title}`}
        heading={`All ${page.title}`}
      />
      <NewColumnWithForm
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`308d12ba-6cc4-44a5-91e2-76d1d714bf23`}
      />
    </>
  );
}

export default function InsightsUnified(props) {
  if (props.isBuilder) {
    const { seo, title, canonical, data, prefix } = props;
    return (
      <>
        <DynamicPageSeo
          title={seo?.title || title}
          description={seo?.description || ""}
          canonical={seo?.canonical || canonical}
          keywords={seo?.keywords || ""}
          robots={seo?.robots || ""}
          json_schema={seo?.json_schema || ""}
          image={seo?.open_graph?.image?.url || ""}
        />
        <ContentBlocks data={data} prefix={prefix} />
      </>
    );
  }

  return props.mode === "post" ? <PostView {...props} /> : <TagView {...props} />;
}

export async function getStaticProps({ params }) {
  const global = (await Global()).toJson();

  // Try POST first
  let postPage = null;
  try {
    postPage = (await Collection("news")).getJsonItem(params.url_slug, "url_slug");
  } catch (_) {
    // ignore
  }

  if (postPage && postPage.url_slug) {
    const news = (await Collection("news"))
      .filter((post) => post.country?.en == "United Kingdom" || post.country?.en == "All")
      .filter(
        (post) =>
          Array.isArray(post.tag?.en) &&
          post.tag.en.some((tag) => postPage.tag?.includes(tag))
      );

    const all_news = news.orderBy("order").getItems().slice(0, 8);
    const related_news = news.orderBy("order").getItems().slice(0, 2);

    return {
      props: { global, mode: "post", page: postPage, all_news, related_news },
    };
  }

  // Fallback to TAG
  let tagPage = null;
  try {
    tagPage = (await Collection("news_tag")).getJsonItem(params.url_slug, "url_slug");
  } catch (_) {
    // ignore
  }
  if (!tagPage) {
    return { notFound: true };
  }

  const tagName = tagPage.title;
  const newsForTag = (await Collection("news"))
    .filter((post) => post.country?.en == "United Kingdom" || post.country?.en == "All")
    .filter((post) => post.tag?.en?.includes(tagName));

  const all_news = newsForTag
    .orderBy((n) => Date.parse(n.date?.en), "desc")
    .getItems();

  return {
    props: { global, mode: "tag", page: tagPage, all_news },
  };
}

export async function getStaticPaths() {
  const pages = useDynamicPages()
    .filter((page) => page.url_slug.startsWith("insights/"))
    .filter((i) => !i.is_draft)
    .map((i) => i.url_slug.split("/").pop());

  const postPaths = (await Collection("news"))
    .filter(
      (post) =>
        !pages.includes(post.url_slug) &&
        !post.external_campaign?.en &&
        (post.country?.en == "United Kingdom" || post.country?.en == "All")
    )
    .getPaths("url_slug");

  const tagPaths = (await Collection("news_tag")).getPaths("url_slug");

  return {
    paths: [...postPaths, ...tagPaths],
    fallback: false,
  };
}
